import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Coins, Sparkles, Trophy, Gift, TicketPercent } from "lucide-react";
import { toast } from "sonner";

const TIER_COLORS = {
  rookie: "bg-zinc-500",
  explorer: "bg-emerald-500",
  guardian: "bg-sky-500",
  legend: "bg-amber-500",
};

export default function Gems() {
  const [wallet, setWallet] = useState(null);
  const [spinStatus, setSpinStatus] = useState(null);
  const [raffle, setRaffle] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [lastSpin, setLastSpin] = useState(null);

  const load = useCallback(async () => {
    try {
      const [w, s, r, lb] = await Promise.all([
        api.get("/gamification/wallet"),
        api.get("/gamification/spin/status"),
        api.get("/gamification/raffle/current"),
        api.get("/gamification/leaderboard"),
      ]);
      setWallet(w.data);
      setSpinStatus(s.data);
      setRaffle(r.data);
      setLeaderboard(lb.data);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const spin = async () => {
    setSpinning(true);
    try {
      const { data } = await api.post("/gamification/spin");
      setLastSpin(data.prize);
      toast.success(`You won ${data.prize.label}!`, { description: `New balance: ${data.balance} gems` });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spin failed");
    } finally {
      setSpinning(false);
    }
  };

  const enterRaffle = async () => {
    try {
      const { data } = await api.post("/gamification/raffle/enter");
      toast.success(data.free ? "Free Finder entry claimed!" : "Raffle entry confirmed!");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not enter");
    }
  };

  if (!wallet) return <div className="p-6 text-muted-foreground">Loading gems…</div>;
  const tier = wallet.tier;

  return (
    <div className="space-y-6" data-testid="gems-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center">
          <Coins className="w-6 h-6 text-amber-500" />
        </div>
        <div>
          <h1 className="text-3xl font-display font-bold">Your gems</h1>
          <p className="text-sm text-muted-foreground">Earn gems on every action. Spin daily. Enter monthly raffle.</p>
        </div>
      </div>

      {/* Balance + Tier */}
      <Card>
        <CardContent className="pt-6 grid md:grid-cols-3 gap-6 items-center">
          <div>
            <div className="text-xs uppercase text-muted-foreground tracking-wide">Balance</div>
            <div className="text-5xl font-display font-bold flex items-center gap-2" data-testid="gems-balance">
              <Coins className="w-8 h-8 text-amber-500" />
              {wallet.balance}
            </div>
          </div>
          <div className="md:col-span-2 space-y-2">
            <div className="flex items-center justify-between">
              <Badge className={`${TIER_COLORS[tier.code]} text-white`}>{tier.label}</Badge>
              <div className="text-xs text-muted-foreground">
                {tier.next_at ? `${tier.gems_to_next} to next tier` : "Max tier reached"}
              </div>
            </div>
            <Progress value={tier.progress_pct || (tier.next_at ? 0 : 100)} />
            {tier.next_at && <div className="text-xs text-muted-foreground">Next unlock at {tier.next_at} gems</div>}
          </div>
        </CardContent>
      </Card>

      {/* Spin + Raffle side by side */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Daily spin</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {lastSpin && (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="text-xs uppercase text-amber-600">You just won</div>
                <div className="text-2xl font-bold" data-testid="spin-last-prize">{lastSpin.label}</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs">
              {spinStatus?.wheel?.map((w, i) => (
                <div key={i} className="p-2 rounded bg-muted flex items-center justify-between">
                  <span>{w.label}</span>
                  <span className="text-muted-foreground">×{w.weight}</span>
                </div>
              ))}
            </div>
            <Button
              onClick={spin}
              disabled={!spinStatus?.can_spin || spinning}
              className="w-full"
              data-testid="spin-btn"
            >
              {spinning ? "Spinning…" : spinStatus?.can_spin ? "Spin now" : "Come back tomorrow"}
            </Button>
            {spinStatus?.next_at && !spinStatus.can_spin && (
              <div className="text-xs text-muted-foreground text-center">
                Next spin available: {new Date(spinStatus.next_at).toLocaleString()}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><TicketPercent className="w-5 h-5 text-primary" /> Monthly raffle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="text-xs uppercase text-muted-foreground">This month's prize</div>
              <div className="text-2xl font-bold" data-testid="raffle-prize">{raffle?.prize_label || "—"}</div>
              <div className="text-xs text-muted-foreground mt-1">Period: {raffle?.period}</div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div>My entries: <b data-testid="raffle-my-entries">{raffle?.my_entries || 0}</b></div>
              <div>Total: <b>{raffle?.total_entries || 0}</b></div>
            </div>
            <div className="text-xs text-muted-foreground">Costs {raffle?.entry_cost_gems || 100} gems per entry. Finder tier gets 1 free entry / month.</div>
            <Button onClick={enterRaffle} className="w-full" data-testid="raffle-enter-btn" disabled={raffle?.status !== "open"}>
              {raffle?.status === "drawn" ? "Winner drawn" : "Enter this month"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Ledger + Leaderboard */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /> Recent activity</CardTitle></CardHeader>
          <CardContent>
            {wallet.ledger.length === 0 ? (
              <div className="text-sm text-muted-foreground">No gems yet — spin to get started.</div>
            ) : (
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {wallet.ledger.map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-sm py-1 border-b border-border/40 last:border-0" data-testid="ledger-row">
                    <div>
                      <div className="capitalize">{e.reason.replace(/_/g, " ")}</div>
                      <div className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</div>
                    </div>
                    <div className={`font-mono ${e.delta > 0 ? "text-emerald-500" : "text-rose-500"}`}>
                      {e.delta > 0 ? "+" : ""}{e.delta}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-primary" /> Leaderboard</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {leaderboard.map((row, i) => (
                <div key={row.user_id} className="flex items-center justify-between text-sm py-1.5 border-b border-border/40 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="w-6 text-muted-foreground font-mono">#{i + 1}</span>
                    <span>{row.name}</span>
                    <Badge variant="outline" className="capitalize text-[10px]">{row.tier}</Badge>
                  </div>
                  <div className="font-mono flex items-center gap-1"><Coins className="w-3 h-3 text-amber-500" />{row.gems}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
